import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Eye, EyeOff, MessageSquare, Sparkles, Loader2, Check } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, IconBtn, inputCls } from "./ui";

function Stars({ n }) {
  return <span className="inline-flex text-[#D9933B]">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} fill={i <= n ? "#D9933B" : "none"} className={i <= n ? "" : "text-black/15"} />)}</span>;
}

export default function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [insight, setInsight] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/reviews"); setReviews(data); };
  useEffect(() => { load(); }, []);
  const list = useMemo(() => reviews.filter((r) => filter === "all" || r.status === filter), [reviews, filter]);
  const setStatus = async (id, status) => { await api.put(`/admin/reviews/${id}/status`, { status }); toast.success(`Review ${status}`); load(); };
  const del = async (id) => { if (!window.confirm("Delete this review?")) return; await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); load(); };

  const counts = { pending: reviews.filter((r) => r.status === "pending").length, approved: reviews.filter((r) => r.status === "approved").length, hidden: reviews.filter((r) => r.status === "hidden").length };

  return (
    <div className="space-y-4" data-testid="admin-reviews">
      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "hidden", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`review-filter-${f}`}
            className={`px-4 py-2 rounded-full text-sm font-medium capitalize ${filter === f ? "bg-[#0D530E] text-white" : "bg-white border border-black/10"}`}>
            {f}{counts[f] != null && counts[f] > 0 && <span className="ml-1.5 text-[11px] opacity-80">({counts[f]})</span>}
          </button>
        ))}
      </div>
      {list.length === 0 && <p className="text-[#5C5C5C]">No reviews here.</p>}
      {list.map((r) => (
        <div key={r.id} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`review-row-${r.id}`}>
          <div className="flex justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Stars n={r.rating} />
                <span className="font-semibold text-[#0D530E]">{r.title || "Review"}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-[#2D7A5D]/10 text-[#2D7A5D]" : r.status === "hidden" ? "bg-black/10 text-[#5C5C5C]" : "bg-[#D9933B]/10 text-[#D9933B]"}`}>{r.status}</span>
              </div>
              <p className="text-sm text-[#5C5C5C] mt-0.5">{r.customer_name} · on <b className="text-[#0D530E]">{r.product_name}</b></p>
              <p className="text-sm mt-2">{r.body}</p>
              {r.admin_response && <p className="text-sm mt-2 bg-[#E7E1B1]/50 rounded-xl p-3"><b>Your reply:</b> {r.admin_response}</p>}
            </div>
            <div className="flex gap-0.5 shrink-0 items-start">
              {r.status !== "approved" && <IconBtn title="Approve" testid={`review-approve-${r.id}`} onClick={() => setStatus(r.id, "approved")}><Check size={16} className="text-[#2D7A5D]" /></IconBtn>}
              {r.status !== "hidden" && <IconBtn title="Hide" testid={`review-hide-${r.id}`} onClick={() => setStatus(r.id, "hidden")}><EyeOff size={15} /></IconBtn>}
              {r.status === "hidden" && <IconBtn title="Show" testid={`review-show-${r.id}`} onClick={() => setStatus(r.id, "approved")}><Eye size={15} /></IconBtn>}
              <Respond review={r} reload={load} />
              <IconBtn title="Product insight" testid={`review-insight-${r.id}`} onClick={() => setInsight(r)}><Sparkles size={15} className="text-[#306D29]" /></IconBtn>
              <IconBtn title="Delete" danger testid={`review-del-${r.id}`} onClick={() => del(r.id)}><Trash2 size={15} /></IconBtn>
            </div>
          </div>
        </div>
      ))}
      {insight && <InsightModal review={insight} onClose={() => setInsight(null)} />}
    </div>
  );
}

function Respond({ review, reload }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(review.admin_response || "");
  const save = async () => { await api.put(`/admin/reviews/${review.id}/respond`, { response: text }); toast.success("Reply saved"); setOpen(false); reload(); };
  return (
    <>
      <IconBtn title="Respond" testid={`review-respond-${review.id}`} onClick={() => setOpen(true)}><MessageSquare size={15} /></IconBtn>
      {open && (
        <Modal title="Respond to review" onClose={() => setOpen(false)} testid="respond-form">
          <textarea className={inputCls} rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a public reply…" data-testid="respond-text" />
          <button onClick={save} data-testid="respond-save" className="mt-3 w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full" style={{ transition: "background-color 0.2s ease" }}>Save reply</button>
        </Modal>
      )}
    </>
  );
}

function InsightModal({ review, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/products/${review.product_id}/insight`).then((r) => setData(r.data)).catch(() => setData({ error: true })); }, [review]);
  return (
    <Modal title="AI product insight" onClose={onClose} testid="insight-modal">
      <p className="font-display text-xl text-[#0D530E] mb-4">{review.product_name}</p>
      {!data ? <p className="text-[#5C5C5C] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Analysing…</p> : data.error ? <p className="text-[#B23B3B]">Could not load insight.</p> : (
        <>
          <div className="grid grid-cols-4 gap-2 mb-4 text-center">
            {[["Orders", data.stats.orders], ["Units", data.stats.units], ["Avg ★", data.stats.avg_rating], ["Reviews", data.stats.reviews]].map(([l, v]) => (
              <div key={l} className="bg-[#E7E1B1]/50 rounded-xl p-3"><p className="font-display text-2xl text-[#0D530E]">{v}</p><p className="text-[11px] text-[#5C5C5C]">{l}</p></div>
            ))}
          </div>
          <div className="bg-[#0D530E] text-[#FBF5DD] rounded-2xl p-4">
            <p className="text-xs uppercase tracking-wider text-[#E7E1B1] mb-1 flex items-center gap-1"><Sparkles size={13} /> Recommendation</p>
            <p className="text-sm">{data.recommendation || "No recommendation available."}</p>
          </div>
        </>
      )}
    </Modal>
  );
}
